/**
 * CancelReason enum
 * 주문 취소 사유 (서버 Kotlin enum과 동기화)
 * moving-visit-application CancelReason.kt 기준
 */

/** 단계 라벨 (breadcrumb용) */
export const CancelReasonStage = {
  LEAD_IN: '리드인',
  PRE_VISIT: '방문 전',
  POST_VISIT: '방문 후',
} as const;

/** Received 상태용 취소 사유 */
export const CancelReasonLeadIn = {
  DUPLICATE: 'DUPLICATE',
  ABSENCE_OVER_3_TIMES: 'ABSENCE_OVER_3_TIMES',
  VISIT_REJECTION: 'VISIT_REJECTION',
  VIDEO_REJECTION: 'VIDEO_REJECTION',
  PRICE_INQUIRY_ONLY: 'PRICE_INQUIRY_ONLY',
  RESUBMISSION: 'RESUBMISSION',
  MOVE_DATE_UNSCHEDULED: 'MOVE_DATE_UNSCHEDULED',
  WRONG_ORDER: 'WRONG_ORDER',
  VISIT_AVAILABLE_SCHEDULE_UNSCHEDULED: 'VISIT_AVAILABLE_SCHEDULE_UNSCHEDULED',
  INFO_MISENTRY: 'INFO_MISENTRY',
  INSUFFICIENT_PACKING: 'INSUFFICIENT_PACKING',
  COMPETITOR_CONTRACT: 'COMPETITOR_CONTRACT',
  LOST_REASON_OTHER: 'LOST_REASON_OTHER',
  LOST_REASON_UNKNOWN: 'LOST_REASON_UNKNOWN',
  RFQ_CONVERSION: 'RFQ_CONVERSION',
} as const;

/** VisitAppointed/Scheduling 상태용 취소 사유 */
export const CancelReasonPreVisit = {
  VISIT_SCHEDULE_ADJUSTMENT_FAILED: 'VISIT_SCHEDULE_ADJUSTMENT_FAILED',
  MOVE_DATE_UNSCHEDULED_PRE_VISIT: 'MOVE_DATE_UNSCHEDULED_PRE_VISIT',
  MOVE_CANCELED: 'MOVE_CANCELED',
  COMPETITOR_CONTRACT_PRE_VISIT: 'COMPETITOR_CONTRACT_PRE_VISIT',
  INSUFFICIENT_PACKING_PRE_VISIT: 'INSUFFICIENT_PACKING_PRE_VISIT',
  VIDEO_REJECTION_DURING_VISIT: 'VIDEO_REJECTION_DURING_VISIT',
  REQUESTER_ABSENCE_DURING_VISIT: 'REQUESTER_ABSENCE_DURING_VISIT',
  LOST_REASON_OTHER_PRE_VISIT: 'LOST_REASON_OTHER_PRE_VISIT',
  LOST_REASON_UNKNOWN_PRE_VISIT: 'LOST_REASON_UNKNOWN_PRE_VISIT',
  VIDEO_SHOOTING_REJECTION: 'VIDEO_SHOOTING_REJECTION',
} as const;

/** 방문 후 단계 취소 사유 (세션 타임라인 등 표시용) */
export const CancelReasonPostVisit = {
  ESTIMATE_VALIDITY_EXPIRED: 'ESTIMATE_VALIDITY_EXPIRED',
  COMPETITOR_CONTRACT_PRICE: 'COMPETITOR_CONTRACT_PRICE',
  COMPETITOR_CONTRACT_TRUST: 'COMPETITOR_CONTRACT_TRUST',
  COMPETITOR_CONTRACT_ACQUAINTANCE: 'COMPETITOR_CONTRACT_ACQUAINTANCE',
  COMPETITOR_CONTRACT_OTHER: 'COMPETITOR_CONTRACT_OTHER',
  COMPETITOR_CONTRACT_UNKNOWN: 'COMPETITOR_CONTRACT_UNKNOWN',
  MOVE_DATE_UNSCHEDULED_POST_VISIT: 'MOVE_DATE_UNSCHEDULED_POST_VISIT',
  MOVE_CANCELED_POST_VISIT: 'MOVE_CANCELED_POST_VISIT',
  LOST_REASON_OTHER_POST_VISIT: 'LOST_REASON_OTHER_POST_VISIT',
  LOST_REASON_UNKNOWN_POST_VISIT: 'LOST_REASON_UNKNOWN_POST_VISIT',
} as const;

/** API enum → 한글 라벨 (Kotlin label 기준) */
export const CancelReasonLabels: Record<string, string> = {
  // Lead-in
  DUPLICATE: '중복',
  ABSENCE_OVER_3_TIMES: '부재 3회 초과',
  VISIT_REJECTION: '방문 거절',
  VIDEO_REJECTION: '촬영 거절',
  PRICE_INQUIRY_ONLY: '가격만 궁금',
  RESUBMISSION: '재접수',
  MOVE_DATE_UNSCHEDULED: '이사 일정 미정',
  WRONG_ORDER: '오주문',
  VISIT_AVAILABLE_SCHEDULE_UNSCHEDULED: '방문 가능 일정 미정',
  INFO_MISENTRY: '정보 오입력',
  INSUFFICIENT_PACKING: '짐 정리 미비',
  COMPETITOR_CONTRACT: '타업체 계약',
  LOST_REASON_OTHER: '기타',
  LOST_REASON_UNKNOWN: '미파악',
  RFQ_CONVERSION: 'RFQ 전환',
  // Pre-Visit
  VISIT_SCHEDULE_ADJUSTMENT_FAILED: '방문 가능 일정 미정',
  MOVE_DATE_UNSCHEDULED_PRE_VISIT: '이사 일정 미정',
  MOVE_CANCELED: '이사 취소',
  COMPETITOR_CONTRACT_PRE_VISIT: '타업체 계약',
  INSUFFICIENT_PACKING_PRE_VISIT: '짐 정리 미비',
  VIDEO_REJECTION_DURING_VISIT: '(방문 중) 촬영 거절',
  REQUESTER_ABSENCE_DURING_VISIT: '(방문 중) 접수자 부재 및 연락두절',
  LOST_REASON_OTHER_PRE_VISIT: '기타',
  LOST_REASON_UNKNOWN_PRE_VISIT: '미파악',
  VIDEO_SHOOTING_REJECTION: '영상 촬영 거절',
  // Post-Visit
  ESTIMATE_VALIDITY_EXPIRED: '피치콜 4회 진행',
  COMPETITOR_CONTRACT_PRICE: '타업체 계약 - 가격',
  COMPETITOR_CONTRACT_TRUST: '타업체 계약 - 신뢰',
  COMPETITOR_CONTRACT_ACQUAINTANCE: '타업체 계약 - 지인 추천',
  COMPETITOR_CONTRACT_OTHER: '타업체 계약 - 기타',
  COMPETITOR_CONTRACT_UNKNOWN: '타업체 계약 - 미파악',
  MOVE_DATE_UNSCHEDULED_POST_VISIT: '이사 일정 미정',
  MOVE_CANCELED_POST_VISIT: '이사 취소',
  LOST_REASON_OTHER_POST_VISIT: '기타',
  LOST_REASON_UNKNOWN_POST_VISIT: '미파악',
};

/** enum key → 단계 */
const CANCEL_REASON_STAGE_MAP: Record<string, keyof typeof CancelReasonStage> = {
  DUPLICATE: 'LEAD_IN',
  ABSENCE_OVER_3_TIMES: 'LEAD_IN',
  VISIT_REJECTION: 'LEAD_IN',
  VIDEO_REJECTION: 'LEAD_IN',
  PRICE_INQUIRY_ONLY: 'LEAD_IN',
  RESUBMISSION: 'LEAD_IN',
  MOVE_DATE_UNSCHEDULED: 'LEAD_IN',
  WRONG_ORDER: 'LEAD_IN',
  VISIT_AVAILABLE_SCHEDULE_UNSCHEDULED: 'LEAD_IN',
  INFO_MISENTRY: 'LEAD_IN',
  INSUFFICIENT_PACKING: 'LEAD_IN',
  COMPETITOR_CONTRACT: 'LEAD_IN',
  LOST_REASON_OTHER: 'LEAD_IN',
  LOST_REASON_UNKNOWN: 'LEAD_IN',
  RFQ_CONVERSION: 'LEAD_IN',
  VISIT_SCHEDULE_ADJUSTMENT_FAILED: 'PRE_VISIT',
  MOVE_DATE_UNSCHEDULED_PRE_VISIT: 'PRE_VISIT',
  MOVE_CANCELED: 'PRE_VISIT',
  COMPETITOR_CONTRACT_PRE_VISIT: 'PRE_VISIT',
  INSUFFICIENT_PACKING_PRE_VISIT: 'PRE_VISIT',
  VIDEO_REJECTION_DURING_VISIT: 'PRE_VISIT',
  REQUESTER_ABSENCE_DURING_VISIT: 'PRE_VISIT',
  LOST_REASON_OTHER_PRE_VISIT: 'PRE_VISIT',
  LOST_REASON_UNKNOWN_PRE_VISIT: 'PRE_VISIT',
  VIDEO_SHOOTING_REJECTION: 'PRE_VISIT',
  ESTIMATE_VALIDITY_EXPIRED: 'POST_VISIT',
  COMPETITOR_CONTRACT_PRICE: 'POST_VISIT',
  COMPETITOR_CONTRACT_TRUST: 'POST_VISIT',
  COMPETITOR_CONTRACT_ACQUAINTANCE: 'POST_VISIT',
  COMPETITOR_CONTRACT_OTHER: 'POST_VISIT',
  COMPETITOR_CONTRACT_UNKNOWN: 'POST_VISIT',
  MOVE_DATE_UNSCHEDULED_POST_VISIT: 'POST_VISIT',
  MOVE_CANCELED_POST_VISIT: 'POST_VISIT',
  LOST_REASON_OTHER_POST_VISIT: 'POST_VISIT',
  LOST_REASON_UNKNOWN_POST_VISIT: 'POST_VISIT',
};

/** Kotlin value → enum key (API가 Kotlin value 반환 시 대비) */
const KOTLIN_VALUE_TO_ENUM_KEY: Record<string, string> = {
  // Lead-in
  중복: 'DUPLICATE',
  '부재 3회': 'ABSENCE_OVER_3_TIMES',
  '방문 거절': 'VISIT_REJECTION',
  '촬영 거절': 'VIDEO_REJECTION',
  '가격만 궁금': 'PRICE_INQUIRY_ONLY',
  재접수: 'RESUBMISSION',
  '이사 일정 미정': 'MOVE_DATE_UNSCHEDULED',
  오주문: 'WRONG_ORDER',
  '방문 가능 일정 미정': 'VISIT_AVAILABLE_SCHEDULE_UNSCHEDULED',
  '정보 오입력': 'INFO_MISENTRY',
  '짐 정리 미비': 'INSUFFICIENT_PACKING',
  '타업체 계약': 'COMPETITOR_CONTRACT',
  기타: 'LOST_REASON_OTHER_PRE_VISIT', // 다수 매핑 가능, 방문 전/후 중 하나로
  미파악: 'LOST_REASON_UNKNOWN_PRE_VISIT',
  'RFQ 전환': 'RFQ_CONVERSION',
  // Pre-Visit (value가 label과 다른 경우)
  '방문 일정 조율 불가': 'VISIT_SCHEDULE_ADJUSTMENT_FAILED',
  '이사 취소': 'MOVE_CANCELED',
  '(방문 중) 촬영 거절': 'VIDEO_REJECTION_DURING_VISIT',
  '(방문 중) 접수자 부재': 'REQUESTER_ABSENCE_DURING_VISIT',
  '영상 촬영 거절': 'VIDEO_SHOOTING_REJECTION',
  // Post-Visit
  '견적 유효기한 경과': 'ESTIMATE_VALIDITY_EXPIRED',
  '타업체 계약 - 가격': 'COMPETITOR_CONTRACT_PRICE',
  '타업체 계약 - 신뢰': 'COMPETITOR_CONTRACT_TRUST',
  '타업체 계약 - 지인 추천': 'COMPETITOR_CONTRACT_ACQUAINTANCE',
  '타업체 계약 - 기타': 'COMPETITOR_CONTRACT_OTHER',
  '타업체 계약 - 미파악': 'COMPETITOR_CONTRACT_UNKNOWN',
};

/**
 * 취소 사유를 breadcrumb 스타일로 변환 (예: "방문 후 / 타업체 계약 - 가격")
 * @param value API enum key 또는 Kotlin value
 */
export function getCancelReasonBreadcrumbLabel(value: string): string {
  if (!value?.trim()) return value ?? '';

  const enumKey =
    CANCEL_REASON_STAGE_MAP[value] !== undefined ? value : KOTLIN_VALUE_TO_ENUM_KEY[value];
  const stageKey = enumKey ? CANCEL_REASON_STAGE_MAP[enumKey] : undefined;
  const label = enumKey ? CancelReasonLabels[enumKey] : CancelReasonLabels[value];

  if (stageKey && label) {
    return `${CancelReasonStage[stageKey]} / ${label}`;
  }
  if (label) {
    return label;
  }
  return value;
}

export type CancelReasonLeadInType = keyof typeof CancelReasonLeadIn;
export type CancelReasonPreVisitType = keyof typeof CancelReasonPreVisit;
export type CancelReasonPostVisitType = keyof typeof CancelReasonPostVisit;
export type CancelReasonType =
  | CancelReasonLeadInType
  | CancelReasonPreVisitType
  | CancelReasonPostVisitType;
